import React, { useRef, useState, useEffect } from "react";
import { wardrobeItems } from "../data/wardrobeItems";
import { ChevronUp, ChevronDown } from "lucide-react";

interface WardrobeCatalogProps {
  onSelect: (category: string, overlayFile: string) => void;
  selectedItems: Record<string, string>;
  unlockedItems: string[];
  /** Optional: parent can handle unlocking flow if a locked item is clicked */
  onLockedClick?: (category: string, overlayFile: string) => void;
}

type HoverTip =
  | {
      x: number;
      y: number;
      cost: number | string;
      unlocked: boolean;
    }
  | null;

export default function WardrobeCatalog({
  onSelect,
  selectedItems,
  unlockedItems,
  onLockedClick,
}: WardrobeCatalogProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const [hoverTip, setHoverTip] = useState<HoverTip>(null);

  const updateScrollState = () => {
    const el = listRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  };

  useEffect(() => {
    updateScrollState();
  }, []);

  const scrollByAmount = (amount: number) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollBy({ top: amount, behavior: "smooth" });
    setHoverTip(null);
    setTimeout(updateScrollState, 400);
  };

  const showTip = (
    ev: React.MouseEvent<HTMLButtonElement>,
    cost: number | string,
    unlocked: boolean
  ) => {
    const rect = (ev.currentTarget as HTMLButtonElement).getBoundingClientRect();
    const x = rect.right + 8;
    const y = rect.top + rect.height / 2;
    setHoverTip({ x, y, cost, unlocked });
  };

  const hideTip = () => setHoverTip(null);

  return (
    <div className="relative bg-gray-50 p-3 rounded-lg shadow-md flex flex-col items-center overflow-visible">
      {/* Scroll Up */}
      {canScrollUp && (
        <button
          onClick={(e) => {
            scrollByAmount(-80);
            (e.currentTarget as HTMLButtonElement).blur();
          }}
          className="absolute top-1 left-1/2 -translate-x-1/2 bg-white border rounded-full p-1 shadow hover:bg-gray-100 z-20 focus:outline-none"
          aria-label="Scroll up"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
      )}

      {/* Scrollable wardrobe list */}
      <div
        ref={listRef}
        onScroll={() => {
          updateScrollState();
          if (hoverTip) setHoverTip(null);
        }}
        className="flex flex-col gap-4 max-h-[360px] p-1 pb-2 relative transition-all
                   overflow-y-auto overflow-x-visible overscroll-contain
                   scrollbar-hide"
      >
        {/* Render items in defined order (no sorting) */}
        {wardrobeItems.map((item) => {
          const overlayFile = `${item.category}/${item.file.replace(
            ".png",
            "_overlay.png"
          )}`;
          const isUnlocked = unlockedItems.includes(overlayFile);
          const isSelected = selectedItems[item.category] === overlayFile;

          const handleClick = () => {
            if (isUnlocked) {
              onSelect(item.category, overlayFile);
            } else {
              onLockedClick?.(item.category, overlayFile);
            }
          };

          return (
            <div
              key={`${item.category}-${item.file}`}
              className="relative flex justify-center overflow-visible"
            >
              <button
                aria-disabled={!isUnlocked}
                tabIndex={isUnlocked ? 0 : -1}
                onClick={handleClick}
                onMouseEnter={(e) => showTip(e, item.cost, isUnlocked)}
                onMouseLeave={hideTip}
                className={`relative w-16 h-16 border rounded-md transition flex items-center justify-center
                  ${isSelected ? "border-blue-500 ring-2 ring-blue-300 bg-blue-50" : "hover:bg-gray-100"}`}
                aria-label={item.displayName}
                title={item.displayName}
              >
                <img
                  src={`/assets/capybara/${item.category}/${item.file}`}
                  alt={item.displayName}
                  className="object-contain w-12 h-12"
                />

                {!isUnlocked && (
                  <>
                    <div className="absolute inset-0 rounded-md bg-white/35 ring-1 ring-black/5 pointer-events-none" />
                    <div className="absolute top-1 right-1 text-[10px] px-1.5 py-0.5 rounded bg-black/50 text-white pointer-events-none select-none">
                      🔒
                    </div>
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Scroll Down */}
      {canScrollDown && (
        <button
          onClick={(e) => {
            scrollByAmount(80);
            (e.currentTarget as HTMLButtonElement).blur();
          }}
          className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-white border rounded-full p-1 shadow hover:bg-gray-100 z-20 focus:outline-none"
          aria-label="Scroll down"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      )}

      {/* Tooltip */}
      {hoverTip && (
        <div
          className={`fixed -translate-y-1/2 rounded shadow-md whitespace-nowrap z-[9999]
                      ${hoverTip.unlocked ? "bg-yellow-400 text-black font-semibold" : "bg-gray-800 text-white"}`}
          style={{
            left: `${hoverTip.x}px`,
            top: `${hoverTip.y}px`,
            minWidth: "60px",
            padding: "4px 8px",
            filter: "drop-shadow(0px 2px 3px rgba(0,0,0,0.25))",
            willChange: "transform, opacity",
            pointerEvents: "none",
          }}
        >
          <div className="flex items-center justify-center gap-1">
            <img src="/assets/ui/coin.png" alt="coin" className="w-4 h-4 drop-shadow-sm" />
            <span className="text-sm">{hoverTip.cost}</span>
          </div>
        </div>
      )}
    </div>
  );
}
